import React, { useLayoutEffect, useRef, useState } from 'react';
import { WrittenLogo } from 'assets/icons';

export const ArrowRightIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
);

const reducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type Copy = { page: number; title: string; subtitle: React.ReactNode };

// Keep the old copy until it has faded out, then reveal the new copy in place.
// Only one heading exists, and quick reversals resolve to the latest page.
const IntroHeading = ({
  page,
  title,
  subtitle,
  progress,
}: Copy & { progress: React.ReactNode }) => {
  const [copy, setCopy] = useState<Copy>({ page, title, subtitle });
  const element = useRef<HTMLElement>(null);
  const latest = useRef<Copy>(copy);
  const previousPage = useRef(page);
  const motion = useRef<Animation[]>([]);
  const frame = useRef(0);
  const revision = useRef(0);
  const transitioning = useRef(false);
  latest.current = { page, title, subtitle };
  const copyElements = () =>
    element.current
      ? [
          element.current.querySelector<HTMLElement>('h1'),
          element.current.querySelector<HTMLElement>('.intro-subtitle'),
        ].filter((node): node is HTMLElement => !!node)
      : [];
  const setOpacity = (opacity: string) =>
    copyElements().forEach((node) => {
      // eslint-disable-next-line no-param-reassign
      node.style.opacity = opacity;
    });
  const stop = () => {
    revision.current += 1;
    cancelAnimationFrame(frame.current);
    frame.current = 0;
    motion.current.forEach((animation) => animation.cancel());
    motion.current = [];
    transitioning.current = false;
    setOpacity('');
  };
  useLayoutEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const settle = () => {
      stop();
      setCopy(latest.current);
    };
    preference.addEventListener('change', settle);
    return () => {
      preference.removeEventListener('change', settle);
      stop();
    };
  }, []);
  useLayoutEffect(() => {
    const changed = previousPage.current !== page;
    previousPage.current = page;
    const nodes = copyElements();
    const node = nodes[0];
    if (!node || typeof node.animate !== 'function' || reducedMotion()) {
      if (transitioning.current) stop();
      if (copy.title !== title || copy.subtitle !== subtitle)
        setCopy(latest.current);
      return;
    }
    if (!changed) {
      if (
        !transitioning.current &&
        (copy.title !== title || copy.subtitle !== subtitle)
      )
        setCopy(latest.current);
      return;
    }
    const { opacity } = getComputedStyle(node);
    stop();
    const version = revision.current;
    transitioning.current = true;
    setOpacity(opacity);
    const out = nodes.map((target) =>
      target.animate([{ opacity }, { opacity: 0 }], {
        duration: 140,
        easing: 'ease-out',
        fill: 'forwards',
      }),
    );
    motion.current = out;
    out[0].onfinish = () => {
      if (version !== revision.current) return;
      setOpacity('0');
      out.forEach((animation) => animation.cancel());
      motion.current = [];
      setCopy(latest.current);
      frame.current = requestAnimationFrame(() => {
        if (version !== revision.current) return;
        frame.current = 0;
        const enter = nodes.map((target) =>
          target.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 240,
            easing: 'ease-in-out',
            fill: 'forwards',
          }),
        );
        motion.current = enter;
        enter[0].onfinish = () => {
          if (version !== revision.current) return;
          setOpacity('');
          enter.forEach((animation) => animation.cancel());
          motion.current = [];
          transitioning.current = false;
          setCopy(latest.current);
        };
      });
    };
  }, [page, title, subtitle]);
  return (
    <section
      ref={element}
      className="intro-heading"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="intro-title-block">
        <nav
          className="intro-step-nav"
          aria-label="Introduction steps"
          aria-live="off"
        >
          {progress}
        </nav>
        <h1>
          <span>{copy.title}</span>
        </h1>
      </div>
      <p className="intro-subtitle">
        <span>{copy.subtitle}</span>
      </p>
    </section>
  );
};

type IntroFrameProps = {
  page: number;
  totalSteps: number;
  title: string;
  subtitle: React.ReactNode;
  nextLabel: string;
  onBack: (() => void) | null;
  onNext: () => void;
  onExit: () => void;
  children: React.ReactNode;
};

// One visual frame for the whole introduction; scene content owns no
// navigation. The prototype's "under-subtitle" layout.
export const IntroFrame = ({
  page,
  totalSteps,
  title,
  subtitle,
  nextLabel,
  onBack,
  onNext,
  onExit,
  children,
}: IntroFrameProps) => {
  const root = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const fit = () => {
      const element = root.current;
      if (!element) return;
      const available =
        window.innerHeight - element.getBoundingClientRect().top - 1;
      element.style.setProperty(
        '--intro-height',
        `${Math.max(0, available)}px`,
      );
    };
    fit();
    window.addEventListener('resize', fit);
    if (page === 0 && document.activeElement?.classList.contains('intro-back'))
      root.current
        ?.querySelector<HTMLElement>('.intro-next')
        ?.focus({ preventScroll: true });
    return () => window.removeEventListener('resize', fit);
  }, [page]);
  const progress = (
    <span
      className="intro-progress"
      role="img"
      aria-label={`Step ${page + 1} of ${totalSteps}`}
    >
      <span className="intro-progress-marks" aria-hidden="true">
        {Array.from({ length: totalSteps }, (_, index) => (
          <i
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={
              (index === page && 'current') || (index < page && 'visited') || ''
            }
          />
        ))}
      </span>
    </span>
  );
  return (
    <div
      ref={root}
      className="gs-intro-frame intro-inline-nav"
      data-intro-page={page}
    >
      <header className="intro-header">
        <span className="brand" aria-label="Generational Story">
          <WrittenLogo />
        </span>
        <button
          type="button"
          className="intro-button intro-text-button"
          onClick={onExit}
        >
          <span>Open my Vault</span>
          <ArrowRightIcon />
        </button>
      </header>
      <IntroHeading
        page={page}
        title={title}
        subtitle={subtitle}
        progress={progress}
      />
      <footer className="intro-footer">
        <nav aria-label="Continue introduction">
          <div className="intro-actions">
            <button
              type="button"
              className="intro-button intro-secondary-button intro-back"
              aria-label="Back"
              disabled={!onBack}
              onClick={onBack ?? undefined}
            >
              <ArrowLeftIcon />
            </button>
            <button
              type="button"
              className="intro-button intro-next"
              onClick={onNext}
            >
              <span>{nextLabel}</span>
              <ArrowRightIcon />
            </button>
          </div>
        </nav>
      </footer>
      <main className="intro-artwork">{children}</main>
    </div>
  );
};
